import { Suspense, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import styles from './AccountCard.module.css';
import messages from './AccountCard.messages';
import ProductType from '../../ui/productType/ProductType';
import { ArrowSVG } from '../../../svgComponents/arrowSVG/index';
import AccountHistoryTable from '../accountHistoryTable/AccountHistoryTable';
import { Modal } from '@iwoca/orion';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';
import { Skeleton } from '@ui';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';

type AccountCardProps = {
    accountId: string;
    productType: string;
    balance: number;
    duration: {
        amount: number;
        unit: string;
    };
};

const AccountCard = ({
    accountId,
    productType,
    balance,
    duration
}: AccountCardProps) => {
    const [areTransactionsOpen, setAreTransactionsOpen] = useState(false);

    const handleModal = () => setAreTransactionsOpen(!areTransactionsOpen);

    const durationInMonths = Math.floor(duration.amount / 30);

    return (
        <>
            <button
                className={styles.accountCardWithHistory}
                onClick={handleModal}
            >
                <span className={styles.accountInfo}>
                    <span className={styles.accountType}>
                        <ProductType
                            productType={productType}
                            durationInMonths={durationInMonths}
                        />
                    </span>

                    <span className={styles.balance}>
                        <span className={styles.balanceLabel}>
                            <FormattedMessage {...messages.balanceLabel} />
                        </span>

                        <span
                            data-testid="outstanding-balance"
                            className={styles.balanceAmount}
                        >
                            <FormattedCurrency value={balance} />
                        </span>
                    </span>
                </span>

                <span className={styles.activeDivider} />

                <span className={styles.accountCTA}>
                    <FormattedMessage {...messages.accountCTA} />
                    <ArrowSVG />
                </span>
            </button>

            <Modal
                style={{ maxWidth: '1000px' }}
                isOpen={areTransactionsOpen}
                onClose={handleModal}
                closeIcon
            >
                <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                    <Suspense
                        fallback={
                            <div
                                className="space-y-l"
                                data-testid="account-history-table-loading"
                            >
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </div>
                        }
                    >
                        <AccountHistoryTable
                            accountId={accountId}
                            productType={productType}
                            durationInMonths={durationInMonths}
                        />
                    </Suspense>
                </ErrorBoundary>
            </Modal>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default AccountCard;
